import type {
  IPercentageFormulaRow,
  IPercentageThresholdSide,
  IPerUnitFormulaRow,
  IPerUnitThresholdSide,
  ITemplateConfig,
  SpecType,
} from '@local/lib';

import { BoundOperator, QualityFormulaType, TierMode } from '@local/lib';

import type { FormulaRow, RangeFormulaRow, ThresholdFormulaRow } from '../quality-formulas/qualityFormulaTypes';
import type { TemplateSpecConfig } from './templateTypes';

import { createDefaultPriceBasis } from '../quality-formulas/qualityFormulaDefaults';
import { buildQualityInputForSpec, fromApiProductPriceBasis } from '../quality-formulas/qualityFormulaRequest';

type MultiRowSpecConfig = Extract<
  ITemplateConfig,
  { formulaType: QualityFormulaType.Percentage | QualityFormulaType.PerUnit }
>;

/**
 * Resolves `percentFactor`/`qualityPricePerUnit` for one UI row from an API row that only ever
 * carries one of the two (a Percentage row's `percentFactor`, or a PerUnit row's
 * `qualityPricePerUnit`) — the other stays `0` since `ThresholdFormulaRow`/`RangeFormulaRow` keep
 * both fields regardless of formula type (see their own doc comments).
 */
const resolveRowRates = (
  row: IPercentageFormulaRow | IPercentageThresholdSide | IPerUnitFormulaRow | IPerUnitThresholdSide,
): Pick<ThresholdFormulaRow, 'percentFactor' | 'qualityPricePerUnit'> =>
  'percentFactor' in row
    ? { percentFactor: row.percentFactor, qualityPricePerUnit: 0 }
    : { percentFactor: 0, qualityPricePerUnit: row.qualityPricePerUnit };

const toThresholdRow = (row: IPercentageThresholdSide | IPerUnitThresholdSide): ThresholdFormulaRow => ({
  adjustmentMode: row.adjustmentMode,
  id: crypto.randomUUID(),
  isFlatAdjustment: row.isFlatAdjustment ?? false,
  perIncrement: row.perIncrement,
  ...resolveRowRates(row),
});

const toRangeRow = (row: IPercentageFormulaRow | IPerUnitFormulaRow): RangeFormulaRow => ({
  ...toThresholdRow(row),
  isFlatAdjustment: row.isFlatAdjustment,
  lowerBound: row.lowerBound,
  lowerBoundOperator: row.lowerBoundOperator,
  upperBound: row.upperBound,
  upperBoundOperator: row.upperBoundOperator ?? BoundOperator.Inclusive,
});

/** Shared regrouping for Percentage/PerUnit, which only differ in which rate field is live (see `resolveRowRates`). */
const fromMultiRowSpecConfig = (config: MultiRowSpecConfig, precision: number): TemplateSpecConfig => {
  const { contractedSpec, formulaType, tierMode } = config;
  const rows: FormulaRow[] =
    config.tierMode === TierMode.Threshold
      ? config.formulaRows.map(toThresholdRow)
      : config.formulaRows.map(toRangeRow);
  // Only Percentage specs carry a Product Price Basis (see ProductPriceBasisSchema in
  // QualityDto.ts) — PerUnit always gets the Contract Price default, same as every other
  // non-Percentage formula type below.
  const priceBasis =
    config.formulaType === QualityFormulaType.Percentage
      ? fromApiProductPriceBasis(config.productPriceBasis)
      : createDefaultPriceBasis();
  return { contractedSpec, formulaType, precision, priceBasis, rows, tierMode };
};

/**
 * Converts a persisted template `config` (the canonical, QualityDto-validated `ITemplateConfig`
 * shape) into this page's editable form state.
 */
export const fromTemplateConfig = (config: ITemplateConfig): TemplateSpecConfig => {
  const { precision } = config;

  // `config` only narrows off a direct `config.<field>` read (not a destructured alias, and not
  // a call to an arbitrary predicate like `isMultiRowFormulaType` — only a literal equality check
  // on the property itself narrows a discriminated union), so every branch below re-reads
  // `config.formulaType` directly.
  if (config.formulaType === QualityFormulaType.Percentage || config.formulaType === QualityFormulaType.PerUnit) {
    return fromMultiRowSpecConfig(config, precision);
  }
  // Single-row formula types (AdjustedSalesPrice, QuantityAdjustedToContracted/Dry) have no
  // top-level contractedSpec/tierMode of their own on the API side — SpecState still carries both
  // fields (ignored for these formula types, see SpecState's own doc comment), so fill in
  // placeholder values a user switching formula type will immediately overwrite.
  if (config.formulaType === QualityFormulaType.AdjustedSalesPrice) {
    const [row] = config.formulaRows;
    return {
      contractedSpec: 0,
      formulaType: config.formulaType,
      precision,
      priceBasis: createDefaultPriceBasis(),
      rows: [{ contractedSpec: row.contractedSpec, id: crypto.randomUUID(), lowerBound: 0 }],
      tierMode: TierMode.Threshold,
    };
  }

  // QuantityAdjustedByPercent (e.g. Nitrogen) has no formulaRows at all — row 0 is a placeholder
  // with both fields unused, same as every other field this formula type ignores.
  if (config.formulaType === QualityFormulaType.QuantityAdjustedByPercent) {
    return {
      contractedSpec: 0,
      formulaType: config.formulaType,
      precision,
      priceBasis: createDefaultPriceBasis(),
      rows: [{ contractedSpec: 0, id: crypto.randomUUID(), lowerBound: 0 }],
      tierMode: TierMode.Threshold,
    };
  }

  const [row] = config.formulaRows;
  return {
    contractedSpec: 0,
    formulaType: config.formulaType,
    precision,
    priceBasis: createDefaultPriceBasis(),
    rows: [{ contractedSpec: 0, id: crypto.randomUUID(), lowerBound: row.lowerBound }],
    tierMode: TierMode.Threshold,
  };
};
/**
 * Converts this page's form state into the canonical, API-persisted `ITemplateConfig` — the
 * inverse of `fromTemplateConfig`. Reuses `buildQualityInputForSpec` (qualityFormulaRequest.ts),
 * which performs this exact per-formula-type regrouping for the quality-adjustments calculate
 * endpoint's request shape; a template's persisted config is that same shape with `actualSpec`
 * swapped for `specType` — the card's own spec (not read off `state`, which has no such field).
 * The API re-syncs `specType` to the template's outer `specType` regardless of what's sent, so
 * any value works, but passing the real one keeps this output self-consistent.
 */
export const toTemplateConfig = (state: TemplateSpecConfig, specType: SpecType): ITemplateConfig => {
  const { actualSpec: _actualSpec, ...formulaSpec } = buildQualityInputForSpec(0, state);
  return { ...formulaSpec, specType };
};
